import { TOGGLE_DEFAULT_TOOLTIP, type ToggleProps } from '@/shared/types/toggle';
import { cn } from '@/shared/utils/cn';
import { useToggle } from '../../hooks/useToggle';
import { forwardRef, useMemo, type ReactElement } from 'react';
import { Icon } from '@/modules/Icon';
import { Tooltip } from '@/modules/Tooltip';

/**
 * Toggle - Componente de toggle/interruptor
 *
 * Componente de toggle que permite activar/desactivar un estado.
 * Soporta labels, iconos, estados controlados/no controlados y diferentes posiciones de label.
 *
 * @example
 * ```tsx
 * // Toggle simple
 * <Toggle
 *   checked={isEnabled}
 *   onChange={(checked) => setIsEnabled(checked)}
 * />
 *
 * // Toggle con label
 * <Toggle
 *   label="Activar notificaciones"
 *   labelPosition="right"
 *   checked={notifications}
 *   onChange={(checked) => setNotifications(checked)}
 * />
 *
 * // Toggle con icono
 * <Toggle
 *   icon="BellOutlined"
 *   checked={isEnabled}
 *   onChange={(checked) => setIsEnabled(checked)}
 * />
 *
 * // Tooltips por estado (por defecto Activo / Inactivo)
 * <Toggle checked={on} onChange={setOn} tooltip={{ enable: 'Encendido', disabled: 'Apagado' }} />
 * ```
 *
 * @see {@link ToggleProps} para la lista completa de props
 */
export const Toggle = forwardRef<HTMLButtonElement, ToggleProps>(
  (
    {
      disabled = false,
      label,
      labelPosition = 'right',
      icon,
      iconColor = 'tertiary',
      iconDuotonePrimary,
      iconDuotoneSecondary,
      iconDuotoneOpacityPrimary,
      iconDuotoneOpacitySecondary,
      stroke = false,
      className,
      name,
      id,
      ariaLabel,
      tooltip,
      ...props
    },
    ref,
  ) => {
    const { isChecked, toggle } = useToggle({
      ...props,
      disabled,
    });

    const tooltipLabels = useMemo(() => {
      if (tooltip === false) return null;
      return { ...TOGGLE_DEFAULT_TOOLTIP, ...(tooltip ?? {}) };
    }, [tooltip]);

    // Clases del contenedor del toggle (32x20px según Figma)
    const toggleContainerClasses = cn(
      // Base según Figma - usando clases de Tailwind configuradas con Positive Space
      'ui-imj',
      'ui-relative ui-inline-flex ui-shrink-0 ui-cursor-pointer ui-rounded-2xl',
      'ui-transition-colors ui-duration-200 ui-ease-in-out',
      'ui-w-32 ui-h-20 ui-px-2 ui-py-0', // w-32 = var(--ui-Positive-space_32), h-20 = var(--ui-Positive-space_20), px-2 = var(--ui-Positive-space_2)
      'ui-items-center',
      'focus:ui-outline-none focus:ui-ring-2 focus:ui-ring-offset-2 focus:ui-ring-fill-brand',
      // Posición del thumb según estado
      {
        'ui-justify-end': isChecked,
        'ui-justify-start': !isChecked,
      },
      // Colores semánticos según estado (del sistema de diseño Figma)
      {
        'ui-bg-fill-brand': isChecked && !disabled, // Activo: var(--ui-color-bg-fill-brand-default)
        'ui-bg-fill-brand-disabled': !isChecked && !disabled, // Inactivo: var(--ui-color-bg-fill-brand-disabled)
        'ui-bg-fill-hover': disabled, // Disabled: var(--ui-color-bg-fill-hover)
      },
      // Border stroke usando token semántico
      {
        'ui-border ui-border-solid ui-border-DEFAULT': stroke, // var(--ui-color-border-default)
      },
      // Estados de interacción
      {
        'ui-cursor-not-allowed ui-opacity-60': disabled,
      },
    );

    // Clases del thumb (16x16px blanco según Figma) - usando clases de Tailwind
    const thumbClasses = cn(
      // Base según Figma
      'ui-pointer-events-none ui-inline-block ui-rounded-control-pill',
      'ui-bg-fill ui-shadow-lg',
      'ui-w-16 ui-h-16', // w-16 = var(--ui-Positive-space_16), h-16 = var(--ui-Positive-space_16)
      'ui-transition-transform ui-duration-200 ui-ease-in-out',
    );

    // Clases del wrapper completo - usando clases de Tailwind
    const wrapperClasses = cn(
      'ui-imj',
      'ui-inline-flex ui-items-center ui-gap-8', // gap-8 = var(--ui-Positive-space_8)
      {
        'ui-flex-row-reverse': labelPosition === 'left',
      },
      className,
    );

    // Clases del label (según Figma: Type/Body/SM/Bold) - usando tokens semánticos
    const labelClasses = cn(
      'ui-font-body-sm-bold ui-text-body-sm-bold ui-leading-body-sm-bold ui-tracking-body-sm',
      'ui-text-primary ui-select-none', // var(--ui-color-text-primary)
      {
        'ui-opacity-50 ui-cursor-not-allowed': disabled,
        'ui-cursor-pointer': !disabled,
      },
    );

    const handleClick = () => {
      if (!disabled) {
        toggle();
      }
    };

    const toggleButton = (
      <button
        ref={ref}
        type="button"
        role="switch"
        aria-checked={isChecked}
        aria-label={ariaLabel || label}
        disabled={disabled}
        name={name}
        id={id}
        onClick={handleClick}
        className={toggleContainerClasses}
      >
        <span className={thumbClasses} />
      </button>
    );

    const withOptionalTooltip = (node: ReactElement) =>
      tooltipLabels ? (
        <Tooltip
          label={isChecked ? tooltipLabels.enable : tooltipLabels.disabled}
          position="top"
          preserveLayout
        >
          {node}
        </Tooltip>
      ) : (
        node
      );

    if (!label && !icon) {
      return withOptionalTooltip(toggleButton);
    }

    return withOptionalTooltip(
      <label className={wrapperClasses}>
        {toggleButton}
        {(label || icon) && (
          <div className="ui-flex ui-items-center ui-gap-2">
            {icon && (
              <Icon
                name={icon}
                size="sm"
                color={iconColor}
                colorDuotonePrimary={iconDuotonePrimary}
                colorDuotoneSecondary={iconDuotoneSecondary}
                opacityDuotonePrimary={iconDuotoneOpacityPrimary}
                opacityDuotoneSecondary={iconDuotoneOpacitySecondary}
              />
            )}
            {label && <span className={labelClasses}>{label}</span>}
          </div>
        )}
      </label>,
    );
  },
);

Toggle.displayName = 'Toggle';
